import { PropsWithChildren, useCallback, useLayoutEffect } from 'react';
import { createContext, useContext, useState } from 'react';
import type { AvailableThemes as AvailableThemesType } from '@/_constants/AvailableThemes.ts';
import AvailableThemes from '@/_constants/AvailableThemes.ts';
import theme from '@/theme/default.ts';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

interface ThemeContextProps {
  activeTheme: AvailableThemesType;
  updateTheme: (theme: AvailableThemesType) => void;
}

const ThemeContext = createContext<ThemeContextProps>(undefined!);

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [activeTheme, setActiveTheme] = useState<AvailableThemesType>(AvailableThemes.DEFAULT);

  const updateTheme = useCallback((theme: AvailableThemesType) => {
    setActiveTheme(theme);
  }, []);

  useLayoutEffect(() => {
    document.querySelector('body')!.setAttribute('data-theme', activeTheme);
  }, [activeTheme]);

  return (
    <ThemeContext.Provider
      value={{
        activeTheme,
        updateTheme,
      }}
    >
      <MuiThemeProvider theme={theme[activeTheme]}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export function useThemeContext(): ThemeContextProps {
  const context = useContext(ThemeContext);

  if (typeof context === 'undefined') {
    throw new Error('useThemeContext should be used within the ThemeContext provider!');
  }

  return context;
}
